type FeatureIconType =
	| 'aggregate'
	| 'alert'
	| 'mail'
	| 'qc'
	| 'team'
	| 'shield'

const FEATURES: {
	num: string
	title: string
	body: string
	icon: FeatureIconType
}[] = [
		{
			num: '01',
			title: 'Agrégation multi-source',
			body: 'RSS, Google News et APIs partenaires. Vigilio rassemble tout ce qui compte dans un seul flux cohérent, nettoyé et horodaté.',
			icon: 'aggregate',
		},
		{
			num: '02',
			title: 'Alertes précises',
			body: 'Mots-clés, exclusions, opérateurs booléens, filtres par source ou région. Créez des alertes qui attrapent le signal et ignorent le bruit.',
			icon: 'alert',
		},
		{
			num: '03',
			title: 'Résumés par courriel',
			body: 'Quotidien, hebdomadaire, en temps réel : choisissez la fréquence. Chaque courriel est structuré, lisible, prêt à être partagé avec votre équipe.',
			icon: 'mail',
		},
		{
			num: '04',
			title: 'Couverture régionale fine',
			body: 'Filtrez par province, région ou ville. Surveillez Montréal, le Saguenay ou Sherbrooke spécifiquement pas seulement les grands titres nationaux.',
			icon: 'qc',
		},
		{
			num: '05',
			title: "Partage d'équipe",
			body: "Alertes partagées, commentaires, archivage structuré. Votre équipe travaille sur la même base, sans dupliquer les abonnements.",
			icon: 'team',
		},
		{
			num: '06',
			title: 'Conforme Loi 25 et LCAP',
			body: "Confirmation d'inscription, désinscription automatique, traçabilité des envois, hébergement au Canada. Respectez la Loi 25 du Québec et la loi canadienne anti-pourriel sans y penser.",
			icon: 'shield',
		},
	]

export function Features() {
	return (
		<section
			id="fonctionnalites"
			style={{ padding: '140px 0 80px', background: 'var(--surface)' }}
		>
			<div className="mx-auto max-w-310 px-5 md:px-10">
				<div className="mx-auto mb-20 max-w-160 text-center">
					<div className="eyebrow mb-6 justify-center">Fonctionnalités</div>
					<h2 className="pb-5">
						Tout ce qu'il faut pour{' '}
						<span className="serif-italic">suivre sans s'épuiser</span>.
					</h2>
					<p
						style={{
							fontSize: 19,
							color: 'var(--text-secondary)',
							lineHeight: 1.5,
						}}
					>
						Vigilio est conçu pour les équipes qui doivent rester à l'affût mais qui n'ont pas le temps de tout lire.
					</p>
				</div>

				<div
					className="grid border-t"
					style={{
						gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
						borderColor: 'var(--border-soft)',
					}}
				>
					{FEATURES.map((f) => (
						<FeatureCard key={f.num} {...f} />
					))}
				</div>
			</div>
		</section>
	)
}

function FeatureCard({
	num,
	title,
	body,
	icon,
}: {
	num: string
	title: string
	body: string
	icon: FeatureIconType
}) {
	return (
		<div
			className="group relative transition-[background] duration-200"
			style={{
				padding: '36px 32px 40px',
				borderBottom: '1px solid var(--border-soft)',
				borderRight: '1px solid var(--border-soft)',
				background: 'white',
			}}
			onMouseEnter={(e) => {
				e.currentTarget.style.background = 'var(--mist)'
			}}
			onMouseLeave={(e) => {
				e.currentTarget.style.background = 'white'
			}}
		>
			<div className="mb-8 flex items-start justify-between">
				<FeatureIcon type={icon} />
				<span
					className="font-mono text-[11px] tracking-widest"
					style={{ color: 'var(--text-secondary)' }}
				>
					{num}
				</span>
			</div>
			<h3 className="pb-3" style={{ fontSize: 22 }}>
				{title}
			</h3>
			<p
				style={{
					fontSize: 15,
					color: 'var(--text-secondary)',
					lineHeight: 1.6,
				}}
			>
				{body}
			</p>
		</div>
	)
}

function FeatureIcon({ type }: { type: FeatureIconType }) {
	const c = '#5D2FF0'
	const iw = 20
	const svgByType: Record<FeatureIconType, React.ReactElement> = {
		aggregate: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<circle cx="5" cy="5" r="2" stroke={c} strokeWidth="1.4" />
				<circle cx="15" cy="5" r="2" stroke={c} strokeWidth="1.4" />
				<circle cx="10" cy="15" r="2" stroke={c} strokeWidth="1.4" />
				<path d="M6.5 6.5L8.5 13.5M13.5 6.5L11.5 13.5M7 5h6" stroke={c} strokeWidth="1.4" />
			</svg>
		),
		alert: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<path
					d="M4 9c0-3.3 2.7-6 6-6s6 2.7 6 6v3.5l1.5 2H2.5L4 12.5V9Z"
					stroke={c}
					strokeWidth="1.4"
					strokeLinejoin="round"
				/>
				<path d="M8 17a2 2 0 0 0 4 0" stroke={c} strokeWidth="1.4" />
			</svg>
		),
		mail: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<rect x="2.5" y="4" width="15" height="12" rx="1.5" stroke={c} strokeWidth="1.4" />
				<path d="M3 5l7 6 7-6" stroke={c} strokeWidth="1.4" strokeLinejoin="round" />
			</svg>
		),
		qc: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<path
					d="M10 2L3 5v5c0 4 3 7 7 8 4-1 7-4 7-8V5l-7-3Z"
					stroke={c}
					strokeWidth="1.4"
					strokeLinejoin="round"
				/>
				<path
					d="M7 9l1.5 3L13 7"
					stroke={c}
					strokeWidth="1.4"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		),
		team: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<circle cx="7" cy="7" r="2.5" stroke={c} strokeWidth="1.4" />
				<circle cx="13.5" cy="8" r="2" stroke={c} strokeWidth="1.4" />
				<path
					d="M2.5 16c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4M13 14c2 0 4 1 4 3"
					stroke={c}
					strokeWidth="1.4"
					strokeLinecap="round"
				/>
			</svg>
		),
		shield: (
			<svg width={iw} height={iw} viewBox="0 0 20 20" fill="none">
				<path
					d="M10 2.5L4 5v5c0 3.5 2.5 6.5 6 7.5 3.5-1 6-4 6-7.5V5l-6-2.5Z"
					stroke={c}
					strokeWidth="1.4"
					strokeLinejoin="round"
				/>
			</svg>
		),
	}
	return (
		<div
			className="flex size-10 items-center justify-center rounded-[10px]"
			style={{
				background:
					'linear-gradient(135deg, rgba(93,47,240,0.08), rgba(123,92,255,0.04))',
				border: '1px solid rgba(93,47,240,0.14)',
			}}
		>
			{svgByType[type]}
		</div>
	)
}
